'use client'

import { useJobStore } from '@stores/job.store'

import { useEffect } from 'react'

import dynamic from 'next/dynamic'
import { useParams } from 'next/navigation'

import { JobPageItem } from '@types'

import Button from '@components/buttons/button'
import useApplyForAJob from '@components/job/hooks/useApplyForAJob'
import { useChatContext } from '@contexts/chat.context'
import withChat from '@hocs/withChat'
import useIsLoggedIn from '@hooks/user/useIsLoggedIn'
import { basePath } from '@lib/constants'

import styles from './apply-and-reminder.module.scss'
import useApplyWithSonicApply from '@components/job/hooks/useApplyWithSonicApply'

const ReminderButton = dynamic(() => import('@components/job/reminder-button'), { ssr: false })

type ApplyAndReminderProps = {
  jobId: string
  isEligibleToApply: boolean
}

const ApplyAndReminder = ({ jobId, isEligibleToApply }: ApplyAndReminderProps): JSX.Element => {
  const isApplied = useJobStore((state) => state.isApplied)
  const setIsApplied = useJobStore((state) => state.setIsApplied)
  const openChat = useApplyForAJob()
  const applyWithSonicApply = useApplyWithSonicApply()
  const { isUserLoggedIn } = useIsLoggedIn()
  const isEasyApply = useJobStore((st) => st.isEasyApply)
  const { locale } = useParams()

  const { setJobIds } = useChatContext()
  const { isLoggedIn } = useIsLoggedIn()

  useEffect(() => {
    const abortController = new AbortController()
    const signal = abortController.signal
    const fetchJob = async (): Promise<JobPageItem | undefined> => {
      try {
        const res = await fetch(`${basePath}/api/${locale}/jobs/fetch-job/${jobId}`, { signal })
        if (!res.ok) {
          return
        }
        return await res.json()
      } catch (error) {
        return
      }
    }

    // if the user is logged in we need to fetch the job again to check if the user has applied
    // if the user is not logged in we can check the store directly
    if (isLoggedIn) {
      fetchJob().then((job) => {
        if (undefined === job) {
          return
        }
        if (job.hasApplied) {
          setIsApplied(true)
        }
      })
    }

    return () => {
      abortController.abort()
    }
  }, [isLoggedIn, jobId, setIsApplied, locale])

  useEffect(() => {
    if (isEasyApply && jobId && isLoggedIn) {
      setJobIds([jobId])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEasyApply, jobId, isLoggedIn])

  return (
    <div className={styles.apply}>
      <Button
        className={styles['apply-btn']}
        variant="yellow"
        size="lg"
        onClick={locale === 'us' ? applyWithSonicApply : openChat}
        disabled={isApplied || !isEligibleToApply}
      >
        {isApplied ? 'Applied!' : 'Apply now'}
      </Button>
      {!isApplied && isUserLoggedIn && <ReminderButton className={styles.reminder} />}
    </div>
  )
}

export default withChat(ApplyAndReminder)
